/**
 * Compact card variants for phones.
 *
 * Mobile is operational, so a phone gets the answer, not the instrument panel:
 * status, what is next, when, and what is stopping it. The full capacity
 * breakdown stays one tap away rather than being scrolled past.
 */
import { Link } from 'react-router-dom';
import type { ProjectTile, TeamMemberTile } from '@dcc/shared';
import { CapacityLayer, Meter, StateChip, Unknown } from './Primitives';
import { toneFor } from '../lib/tone';
import { relativeTime } from '../lib/format';

export function CompactProjectTile({ tile }: { tile: ProjectTile }) {
  const tone =
    tile.captain?.state === 'SPLIT_BRAIN_DETECTED' ? 'critical' : toneFor('project', tile.status);
  return (
    <article className="card card--toned stack stack--tight" data-tone={tone}>
      <div className="spread">
        <Link to={`/projects/${tile.id}/control`} style={{ fontWeight: 600, fontSize: 16 }}>
          {tile.name}
        </Link>
        <StateChip kind="project" state={tile.status} />
      </div>
      <p className="tiny dim">{tile.statusReason}</p>

      <Meter
        value={tile.journeyCompletionPercent}
        tone={tile.status === 'BLOCKED' ? 'critical' : 'active'}
        ariaLabel={`${tile.name} journey completion`}
      />
      <div className="spread tiny">
        <span className="dim">
          {tile.journeyCompletionPercent}% · {tile.versionKey ?? 'no version'}
        </span>
        <span className="dim">
          {tile.activeAgents} agent{tile.activeAgents === 1 ? '' : 's'}
        </span>
      </div>

      <div className="small">
        <div className="tile__row">
          <span className="dim">Next testable</span>
          <span className="truncate">{tile.nextStationLabel ?? 'none scheduled'}</span>
        </div>
        <div className="tile__row">
          <span className="dim">Finish</span>
          <span>
            {tile.forecast.etaAt ? relativeTime(tile.forecast.etaAt) : <Unknown />}
            <span className="dim"> · {tile.forecast.confidence.toLowerCase()}</span>
          </span>
        </div>
      </div>

      {(tile.openBlockers > 0 || tile.openDecisions > 0) && (
        <div className="inline inline--tight">
          {tile.openBlockers > 0 && (
            <span className="chip" data-tone="critical">
              {tile.openBlockers} blocker{tile.openBlockers === 1 ? '' : 's'}
            </span>
          )}
          {tile.openDecisions > 0 && (
            <span className="chip" data-tone="risk">
              {tile.openDecisions} decision{tile.openDecisions === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      <details>
        <summary className="tiny dim" style={{ cursor: 'pointer', minHeight: 32, paddingTop: 8 }}>
          Capacity, captain and connection
        </summary>
        <div className="stack stack--tight" style={{ marginTop: 10 }}>
          <CapacityLayer layer={tile.sessionRange} title="Session range" compact />
          <CapacityLayer layer={tile.weeklyFuel} title="Weekly fuel" compact />
          <div className="inline inline--tight">
            {tile.captain ? (
              <StateChip
                kind="lease"
                state={tile.captain.state}
                label={`captain epoch ${tile.captain.epoch}`}
              />
            ) : (
              <StateChip kind="lease" state="NONE" label="no captain" />
            )}
            <StateChip kind="connection" state={tile.connectionState} />
          </div>
        </div>
      </details>

      <div className="inline inline--tight">
        <Link className="btn btn--sm" to={`/projects/${tile.id}/control`}>
          Control
        </Link>
        <Link className="btn btn--sm btn--quiet" to={`/projects/${tile.id}/journey`}>
          Journey
        </Link>
      </div>
    </article>
  );
}

export function CompactCrewTile({ member }: { member: TeamMemberTile }) {
  const tone = toneFor('member', member.status);
  return (
    <article className="card card--toned stack stack--tight" data-tone={tone}>
      <div className="spread">
        <div>
          <strong>{member.name}</strong>
          <div className="tiny dim">{member.role}</div>
        </div>
        <StateChip kind="member" state={member.status} />
      </div>
      <p className="tiny dim">{member.statusDetail}</p>
      <div className="grid grid--2" style={{ gap: 10 }}>
        <CapacityLayer layer={member.sessionRange} title="Session range" compact />
        <CapacityLayer layer={member.weeklyFuel} title="Weekly fuel" compact />
      </div>
      <div className="spread tiny dim">
        <span className="truncate">{member.currentProjectName ?? 'not on a project'}</span>
        <span>
          {member.handoffEligible
            ? 'can take over'
            : (member.handoffBlockedReason ?? 'not eligible')}
        </span>
      </div>
    </article>
  );
}
